/**
 * The composer that starts a chat (the new-chat home and a project's home).
 * The same floating glass composer the chat uses; the first send hands over
 * to the chat, which goes on in the project. The parent owns the words and the
 * Quick Create pills, so a Use it can add its pill to the box.
 */
import { useEffect, useRef, useState } from "react";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { ReasoningEffort } from "@/types/chat.type";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";
import { useWorkshopQuickCreate } from "../api/useWorkshopQuickCreate";
import { addPick, refusedIds, refusedPick, removePick, unavailablePicks } from "../quickCreate";
import { useOptionalWorkshopDesk } from "./workshopDesk";
import { QuickCreateGone } from "./QuickCreateParts";
import WorkshopComposer from "./WorkshopComposer";
import { WORKSHOP_ATTACH_ACCEPT, WORKSHOP_DOC_ACCEPT, WORKSHOP_PHOTO_ACCEPT } from "./workshopAccept";
import { useWorkshopModelPick } from "./useWorkshopModelPick";
import type { ComposerVoice } from "../../agent/rail/RailComposer";

/** What the first send carries into the new chat. */
export type WorkshopBootstrapPayload = {
  firstMessage: string;
  modelId: string;
  reasoningEffort: ReasoningEffort;
  /** The skills and agents it starts (its pills); the words may then be empty. */
  quickCreates?: WorkshopQuickCreateRef[];
};

type Props = {
  text: string;
  onTextChange: (text: string) => void;
  placeholder: string;
  inputLabel: string;
  onStart: (payload: WorkshopBootstrapPayload) => Promise<void>;
  /** Attaching starts the chat, then the chat uploads the files. */
  onAttachFiles?: (files: File[]) => void;
  disabled?: boolean;
  /** The + shows but waits (no project to put a file in yet). */
  attachDisabled?: boolean;
  /** Put the caret in the box (a starting point, a deep link). */
  focusSeq?: number;
  /** The pills in the box, and who hears them change (a pick, "/", a ×). */
  quickCreates?: WorkshopQuickCreateRef[];
  onQuickCreatesChange?: (items: WorkshopQuickCreateRef[]) => void;
  /** Talk, then check the words (Home). */
  voice?: ComposerVoice;
  /** Home has no Workshop desk: the brand the "/" list reads. */
  brandId?: string | null;
};

const NO_PICKS: WorkshopQuickCreateRef[] = [];

export default function WorkshopStartComposer({
  text,
  onTextChange,
  placeholder,
  inputLabel,
  onStart,
  onAttachFiles,
  disabled = false,
  attachDisabled = false,
  focusSeq = 0,
  quickCreates = NO_PICKS,
  onQuickCreatesChange,
  voice,
  brandId,
}: Props) {
  const isPhone = useMediaQuery("(max-width: 767px)");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [starting, setStarting] = useState(false);
  const pick = useWorkshopModelPick();
  const busy = starting || disabled;
  const desk = useOptionalWorkshopDesk();
  const qc = useWorkshopQuickCreate(onQuickCreatesChange ? (desk?.brandId ?? brandId ?? null) : null);
  const qcItems = qc.data?.items;
  const [refused, setRefused] = useState<string[]>([]);
  // Gone from the list (or refused on send): those pills grey and Send waits.
  const unavailable = unavailablePicks(quickCreates, qcItems, refused);

  useEffect(() => {
    if (!focusSeq) return;
    const frame = window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusSeq]);

  const send = () => {
    const message = text.trim();
    const picks = quickCreates;
    if ((!message && picks.length === 0) || unavailable.length > 0 || busy || !pick.model) return;
    onTextChange("");
    setStarting(true);
    void onStart({
      firstMessage: message,
      modelId: pick.model.id,
      reasoningEffort: pick.effort,
      quickCreates: picks,
    }).catch((error) => {
      onTextChange(message);
      setStarting(false);
      const gone = refusedPick(error);
      if (gone && picks.length > 0) {
        setRefused((prev) => [...prev, ...refusedIds(gone, picks)]);
        return;
      }
      queue.add({
        title: "Couldn't start the chat",
        description: getAiRequestErrorMessage(error),
        variant: "error",
      });
    });
  };

  const pickItem = (item: WorkshopQuickCreateItem) => {
    onQuickCreatesChange?.(addPick(quickCreates, item));
  };

  return (
    <>
      <QuickCreateGone items={unavailable} />
    <WorkshopComposer
      value={text}
      onChange={onTextChange}
      onSubmit={send}
      placeholder={placeholder}
      inputLabel={inputLabel}
      disabled={busy || !pick.model}
      isStreaming={false}
      onStop={() => {}}
      isStopping={false}
      sendWhileStreaming={false}
      queueing={false}
      showModel
      models={pick.models}
      selectedModel={pick.model}
      onModelChange={pick.setModel}
      effort={pick.effort}
      onEffortChange={pick.setEffort}
      textareaRef={textareaRef}
      onAttachFiles={onAttachFiles}
      attachDisabled={busy || attachDisabled}
      attachAccept={WORKSHOP_ATTACH_ACCEPT}
      photoAccept={WORKSHOP_PHOTO_ACCEPT}
      fileAccept={WORKSHOP_DOC_ACCEPT}
      staged={[]}
      queued={false}
      isPhone={isPhone}
      quickCreates={quickCreates}
      unavailableQuickCreates={unavailable}
      onRemoveQuickCreate={
        onQuickCreatesChange ? (item) => onQuickCreatesChange(removePick(quickCreates, item)) : undefined
      }
      quickCreateItems={onQuickCreatesChange ? qcItems : undefined}
      onPickQuickCreate={onQuickCreatesChange ? pickItem : undefined}
      voice={voice}
    />
    </>
  );
}

